import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { useAuth } from '../../AuthContext'
import ModalOverlay from './ModalOverlay'

// 共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
const INPUT = 'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const TH = 'text-secondary border-b border-secondary px-2.5 py-1.5 text-left text-[11px] font-semibold whitespace-nowrap'
const TD = 'text-primary px-2.5 py-1.5 align-middle'
const LABEL = 'text-secondary text-[11px] font-medium'
const FIELD = 'flex flex-col gap-1'

// ベクトル化データ（データ基盤 ＞ データ理解 ＞ ベクトル化データ。旧: AI購入支援＞ベクトル化データ管理）
export default function AIPurchaseAssistVectorSources() {
  const { siteId } = useParams()
  const { authHeaders } = useAuth()

  const [sources, setSources] = useState([])
  const [health, setHealth] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [providers, setProviders] = useState([])
  const [defaultProvider, setDefaultProvider] = useState('stub')
  const [showSearch, setShowSearch] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [autoCreatedCount, setAutoCreatedCount] = useState(0)

  const fetchAll = async () => {
    setLoading(true)
    setError(null)
    try {
      const [r1, r2] = await Promise.all([
        fetch(`/api/sites/${siteId}/purchase-assist/vector-sources`, { headers: authHeaders() }),
        fetch(`/api/sites/${siteId}/purchase-assist/vector-sources/embedding-providers`, { headers: authHeaders() }),
      ])
      if (!r1.ok) throw new Error(`一覧取得失敗 HTTP ${r1.status}`)
      const d1 = await r1.json()
      setSources(d1.items || [])
      setHealth(d1.health || null)
      setAutoCreatedCount(d1.auto_created_hub_defaults || 0)
      if (r2.ok) {
        const d2 = await r2.json()
        setProviders(d2.providers || [])
        setDefaultProvider(d2.default || 'stub')
      }
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchAll() }, [siteId])

  // 削除した標準ソースを復元したい場合の手動投入（既存があればスキップ）
  const restoreHubDefaults = async () => {
    if (!window.confirm('W2 Hub標準マスタ連動ソース（商品/レビュー/カテゴリ/特集）を再投入します。\n既存ソースは保持されます。よろしいですか？')) return
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/vector-sources/seed-defaults`, {
      method: 'POST', headers: authHeaders(),
    })
    if (r.ok) {
      const d = await r.json()
      if (d.created > 0) {
        alert(`✓ ${d.created} 件を復元しました`)
      } else {
        alert('全て揃っているため新規追加なし')
      }
      fetchAll()
    } else {
      alert('再投入失敗')
    }
  }

  const SOURCE_TYPE_LABEL = {
    product: { label: '🛍 商品マスタ', accent: 'var(--color-fill-sky)' },
    brand_category: { label: '🏷📁 ブランド・カテゴリ', accent: 'var(--color-fill-pink)' },
    review: { label: '⭐ レビュー / UGC', accent: 'var(--color-fill-yellow)' },
    feature_page: { label: '📄 特集ページ', accent: 'var(--color-fill-green)' },
    custom: { label: '⚙️ カスタム', accent: 'var(--color-fill-tertiary)' },
  }

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='🧠 ベクトル化データ'
        rightNode={
          <div className='flex flex-wrap items-center gap-1.5'>
            <Button color={ButtonColor.PRIMARY} onClick={() => setShowSearch(true)}>🔍 検索品質テスト</Button>
            <Button color={ButtonColor.SECONDARY} onClick={() => setShowCreate(true)}>+ カスタムソース追加</Button>
            {/* W2 Hub標準ソースを誤って削除した場合の復元用 */}
            <span title='削除した標準ソースを復元'>
              <Button color={ButtonColor.SECONDARY} onClick={restoreHubDefaults}>🔁 W2 Hub標準を復元</Button>
            </span>
            <Button color={ButtonColor.SECONDARY} onClick={fetchAll}>🔄 更新</Button>
          </div>
        }
      />
      <p className='text-secondary mb-4 text-xs'>
        商品検索用ベクトル空間の健全性管理。<strong className='text-success'>W2 Hub標準マスタ（商品・レビュー・カテゴリ・特集ページ）は自動接続済み</strong>。同期ボタンを押すだけでAI検索が利用可能になります。
      </p>

      {/* 初回アクセス時のお知らせ */}
      {autoCreatedCount > 0 && (
        <div className='bg-surface-success text-success mb-3 rounded-lg border border-secondary p-3 text-xs'>
          ✨ <strong>初回セットアップ完了</strong>: W2 Hub標準マスタ連動ソース {autoCreatedCount} 件を自動投入しました。各ソースの「🔄 同期実行」を押してベクトル化を開始してください。
        </div>
      )}

      {error && (
        <div className='bg-surface-critical text-critical mb-3 rounded-lg p-2.5 text-xs'>
          取得エラー: {error}
        </div>
      )}

      {/* ヘルス概況 + 容量プログレスバー */}
      {health && (
        <div className={`${CARD} mb-3.5 p-3.5`}>
          <h3 className='text-emphasis m-0 mb-2 text-xs font-bold'>
            🩺 ベクトルDB状況
          </h3>
          <div className='mb-2.5 flex flex-wrap items-center gap-3.5'>
            <span className='text-primary text-base font-bold'>{health.total_vectors.toLocaleString()} ベクトル</span>
            <span className='text-tertiary text-[11px]'>backend: {health.backend}</span>
          </div>
          {/* 容量プログレスバー（10万件を上限と仮定し進捗表示） */}
          {(() => {
            const cap = 100000  // SQLite phase1 想定上限
            const usage = Math.min(100, health.total_vectors / cap * 100)
            const barClass = usage > 80 ? 'bg-fill-critical' : usage > 50 ? 'bg-fill-warning' : 'bg-fill-success'
            const textClass = usage > 80 ? 'text-critical' : usage > 50 ? 'text-warning' : 'text-success'
            return (
              <div className='mb-2.5'>
                <div className='text-tertiary mb-1 flex justify-between text-[10px]'>
                  <span>容量使用率（上限 {cap.toLocaleString()} ベクトル想定）</span>
                  <span className={`font-bold ${textClass}`}>{usage.toFixed(1)}%</span>
                </div>
                <div className='bg-surface-secondary h-1.5 overflow-hidden rounded-full'>
                  <div className={`h-full ${barClass}`} style={{ width: `${usage}%` }} />
                </div>
              </div>
            )
          })()}
          <div className='flex flex-wrap gap-2'>
            {health.breakdown.map((b, i) => (
              <span key={i} className='bg-surface-emphasis text-emphasis rounded px-2 py-0.5 text-[10px] font-bold'>
                {SOURCE_TYPE_LABEL[b.entity_type]?.label || b.entity_type}: {b.count}件 ({b.embedding_dim}次元)
              </span>
            ))}
          </div>
        </div>
      )}

      {/* データソース一覧 */}
      {loading ? (
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      ) : sources.length === 0 ? (
        <div className={`${CARD} text-tertiary p-8 text-center text-xs`}>
          データソースが登録されていません。<br />「📦 標準ソース一括投入」または「+ データソース追加」から開始してください。
        </div>
      ) : (
        <div className='flex flex-col gap-2.5'>
          {sources.map(s => (
            <SourceRow
              key={s.id}
              source={s}
              siteId={siteId}
              authHeaders={authHeaders}
              onChanged={fetchAll}
              providers={providers}
              typeLabel={SOURCE_TYPE_LABEL[s.source_type]}
            />
          ))}
        </div>
      )}

      {/* 検索テストオーバーレイ */}
      {showSearch && (
        <SearchTestOverlay siteId={siteId} authHeaders={authHeaders} onClose={() => setShowSearch(false)} />
      )}

      {/* 新規ソース作成オーバーレイ */}
      {showCreate && (
        <CreateSourceOverlay
          siteId={siteId}
          authHeaders={authHeaders}
          providers={providers}
          defaultProvider={defaultProvider}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchAll() }}
        />
      )}
    </Main>
  )
}


// =====================
// データソース行
// =====================
function SourceRow({ source, siteId, authHeaders, onChanged, providers, typeLabel }) {
  const [syncing, setSyncing] = useState(false)
  const [showConfig, setShowConfig] = useState(false)
  const [draft, setDraft] = useState({ ...source, config: source.config || {} })

  const STATUS_COLOR = {
    pending: { cls: 'text-tertiary', label: '未同期' },
    in_progress: { cls: 'text-warning', label: '🔄 同期中' },
    success: { cls: 'text-success', label: '✓ 成功' },
    partial_failed: { cls: 'text-warning', label: '⚠ 一部失敗' },
    failed: { cls: 'text-critical', label: '✗ 失敗' },
  }
  const sc = STATUS_COLOR[source.last_sync_status] || STATUS_COLOR.pending
  const successRate = source.target_count > 0 ? Math.round(source.vectorized_count / source.target_count * 100) : 0
  const isHubDefault = source.config?.is_hub_default === true
  const hubTable = source.config?.hub_table

  const sync = async () => {
    setSyncing(true)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/vector-sources/${source.id}/sync`, {
        method: 'POST', headers: authHeaders(),
      })
      const d = await r.json()
      if (r.ok) {
        const stats = d.stats || {}
        alert(`同期完了\n対象 ${stats.target} 件 / 成功 ${stats.success} 件 / 失敗 ${stats.failed} 件`)
      } else {
        alert(`同期失敗: ${d.detail || r.status}`)
      }
      onChanged()
    } catch (e) {
      alert(`同期エラー: ${e.message}`)
    } finally {
      setSyncing(false)
    }
  }

  const remove = async () => {
    if (!window.confirm(`「${source.name}」を削除します。\n紐づく ${source.vectorized_count} 件のベクトルも削除されます。`)) return
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/vector-sources/${source.id}`, {
      method: 'DELETE', headers: authHeaders(),
    })
    if (r.ok) onChanged()
    else alert('削除失敗')
  }

  const toggleEnabled = async () => {
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/vector-sources/${source.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ enabled: !source.enabled }),
    })
    if (r.ok) onChanged()
  }

  const saveConfig = async () => {
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/vector-sources/${source.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({
        name: draft.name,
        sync_interval_hours: draft.sync_interval_hours,
        config: draft.config,
      }),
    })
    if (r.ok) {
      setShowConfig(false)
      onChanged()
    } else {
      alert('保存失敗')
    }
  }

  return (
    <div
      className={`${CARD} p-3.5`}
      style={{
        borderLeft: `3px solid ${typeLabel?.accent || 'var(--color-fill-tertiary)'}`,
        opacity: source.enabled ? 1 : 0.55,
      }}
    >
      <div className='flex flex-wrap items-start gap-2.5'>
        <div className='min-w-[240px] flex-1'>
          <div className='mb-1 flex flex-wrap items-center gap-2'>
            <span className='text-tertiary font-mono text-[9px]'>#{source.id}</span>
            <span className='bg-surface-emphasis text-emphasis rounded px-2 py-0.5 text-[10px] font-bold'>{typeLabel?.label || source.source_type}</span>
            {isHubDefault && (
              <span
                className='bg-surface-success text-success rounded border border-secondary px-2 py-0.5 text-[10px] font-bold'
                title={`W2 Hub標準マスタと連動 (${hubTable || ''})`}
              >📦 W2 Hub連動</span>
            )}
            <span className={`bg-surface-secondary rounded px-2 py-0.5 text-[10px] font-bold ${sc.cls}`}>
              {sc.label}
            </span>
            {!source.enabled && <span className='bg-surface-secondary text-tertiary rounded px-1.5 py-0.5 text-[9px] font-bold'>無効</span>}
          </div>
          <div className='text-primary mb-1 text-sm font-semibold'>{source.name}</div>
          {source.config?.description && (
            <div className='text-secondary mb-1.5 text-[11px] leading-relaxed'>
              {source.config.description}
            </div>
          )}
          {(source.config?.hub_tables || source.config?.hub_table) && (
            <div className='mb-1.5 flex flex-wrap gap-1'>
              <span className='text-tertiary mr-1 text-[9px]'>取得元:</span>
              {(() => {
                const breakdown = source.last_sync_breakdown || {}
                const tables = source.config?.hub_tables || [source.config?.hub_table].filter(Boolean)
                return tables.map((t, i) => {
                  const stats = breakdown[t] || {}
                  const fetched = stats.fetched
                  const vectorized = stats.vectorized
                  const linked = stats.linked
                  const hasStats = fetched !== undefined && fetched !== null
                  const label = stats.label || ''
                  // 表示形式: "テーブル名 X件" or "テーブル名 vec:X/Y" or "テーブル名 link:X件"
                  let countText = ''
                  if (hasStats) {
                    if (vectorized !== undefined && vectorized !== null) {
                      countText = ` ${vectorized}/${fetched}`
                    } else if (linked !== undefined && linked !== null) {
                      // linked は「ユニーク参照件数」なので fetched との比率は意味がある
                      // ただし fetched が0でも linked が >0 のケース（後付けマスタ）もあるので注意
                      countText = fetched > 0 ? ` ${linked}/${fetched}使用` : ` ${linked}件`
                    } else {
                      countText = ` ${fetched}件`
                    }
                  }
                  const isUntouched = hasStats && fetched === 0
                  return (
                    <span key={i}
                      title={label ? `${label}${countText ? ' / ' + countText.trim() : ''}` : t}
                      className={`rounded border px-1.5 py-px font-mono text-[9px] font-semibold ${isUntouched ? 'bg-surface-secondary text-tertiary border-secondary' : 'bg-surface-emphasis text-emphasis border-secondary'}`}
                    >
                      {t}
                      {hasStats && (
                        <span className={`ml-1 font-bold ${isUntouched ? 'text-tertiary' : 'text-success'}`}>
                          {countText}
                        </span>
                      )}
                    </span>
                  )
                })
              })()}
            </div>
          )}

          {/* テーブル別の詳細内訳（同期実行後のみ表示） */}
          {source.last_sync_breakdown && Object.keys(source.last_sync_breakdown).length > 0 && (
            <details className='mb-1.5'>
              <summary className='text-emphasis cursor-pointer text-[10px] select-none'>
                📊 テーブル別の取り込み内訳
              </summary>
              <div className='bg-surface-secondary mt-1.5 rounded p-2 text-[10px]'>
                <div className='overflow-x-auto'>
                  <table className='w-full border-collapse'>
                    <thead>
                      <tr>
                        <th className={TH}>テーブル</th>
                        <th className={TH}>役割</th>
                        <th className={`${TH} text-right`}>取得件数</th>
                        <th className={`${TH} text-right`}>反映件数</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(source.last_sync_breakdown).map(([tname, stats]) => {
                        const fetched = stats?.fetched ?? 0
                        const vectorized = stats?.vectorized
                        const linked = stats?.linked
                        const used = vectorized ?? linked ?? 0
                        const isMain = vectorized !== undefined && vectorized !== null
                        // メインテーブル(vectorized): 比率 = vectorized / fetched
                        // 関連テーブル(linked): 「N件中M件が参照された」表示
                        const ratio = (fetched > 0) ? (used / fetched * 100) : 0
                        const ratioCapped = Math.min(100, ratio)
                        return (
                          <tr key={tname} className='border-b border-secondary'>
                            <td className={`${TD} text-emphasis font-mono`}>{tname}</td>
                            <td className={`${TD} text-secondary`}>{stats?.label || '-'}</td>
                            <td className={`${TD} text-right`}>{fetched.toLocaleString()}</td>
                            <td className={`${TD} text-right ${ratioCapped > 0 ? 'text-success' : 'text-tertiary'}`}>
                              {used.toLocaleString()}
                              {fetched > 0 && (
                                <span className='text-tertiary ml-1'>
                                  ({isMain ? `${ratioCapped.toFixed(0)}%ベクトル化` : `${ratioCapped.toFixed(0)}%使用`})
                                </span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </details>
          )}
          <div className='text-secondary flex flex-wrap gap-3.5 text-[11px]'>
            <span>対象: <strong className='text-primary'>{source.target_count}</strong></span>
            <span>ベクトル化済: <strong className='text-success'>{source.vectorized_count}</strong></span>
            {source.failed_count > 0 && <span>失敗: <strong className='text-critical'>{source.failed_count}</strong></span>}
            <span>達成率: <strong>{successRate}%</strong></span>
            <span>同期間隔: <strong>{source.sync_interval_hours}h</strong> <span className='text-tertiary'>（自動）</span></span>
            {source.last_synced_at && (
              <>
                <span>最終: {new Date(source.last_synced_at).toLocaleString('ja-JP')}</span>
                {(() => {
                  const elapsedMs = Date.now() - new Date(source.last_synced_at).getTime()
                  const elapsedH = Math.floor(elapsedMs / 3600000)
                  const intervalH = source.sync_interval_hours || 24
                  const remainingH = intervalH - elapsedH
                  if (remainingH <= 0) {
                    return <span className='text-warning'>⏰ 次回同期待ち</span>
                  }
                  return <span className='text-tertiary'>次回まで約{remainingH}h</span>
                })()}
              </>
            )}
          </div>
          {/* プロバイダを変えたのに再同期していない状態。意味検索が効かず、
              語句検索だけで候補が出るため、黙って劣化させずに知らせる */}
          {source.provider_mismatch && (
            <div className='bg-surface-caution text-caution mt-1.5 rounded p-1.5 text-[10px]'>
              ⚠ 埋め込みプロバイダを変更しましたが、まだ再同期していません。
              いまの索引は <strong>{(source.stored_models || []).join(' / ')}</strong> で作られており、
              現在の設定は <strong>{source.current_model}</strong> です。
              この状態では意味検索が効かず、語句の一致だけで候補が出ます。
              <strong>「🔄 同期実行」で作り直してください。</strong>
            </div>
          )}
          {source.last_error && (
            <div className='bg-surface-critical text-critical mt-1.5 rounded p-1.5 text-[10px]'>
              ⚠ {source.last_error}
            </div>
          )}
        </div>
        <div className='flex flex-wrap gap-1.5'>
          <Button color={ButtonColor.PRIMARY} disabled={syncing || !source.enabled} onClick={sync}>
            {syncing ? '⏳ 同期中...' : '🔄 同期実行'}
          </Button>
          <Button color={ButtonColor.SECONDARY} onClick={toggleEnabled}>{source.enabled ? '無効化' : '有効化'}</Button>
          <Button color={ButtonColor.SECONDARY} onClick={() => setShowConfig(!showConfig)}>⚙️ 設定</Button>
          <Button color={ButtonColor.CRITICAL} onClick={remove}>削除</Button>
        </div>
      </div>

      {showConfig && (
        <div className='bg-surface-secondary mt-3 rounded-lg border border-secondary p-3'>
          <div className='grid grid-cols-[1fr_120px_200px] gap-2'>
            <Field label='表示名'>
              <input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} className={INPUT} />
            </Field>
            <Field label='同期間隔(h)'>
              <input type='number' min={1} max={168} value={draft.sync_interval_hours} onChange={e => setDraft({ ...draft, sync_interval_hours: parseInt(e.target.value) || 24 })} className={INPUT} />
            </Field>
            <Field label='埋め込みプロバイダー'>
              <select
                value={draft.config?.embedding_provider || 'stub'}
                onChange={e => setDraft({ ...draft, config: { ...draft.config, embedding_provider: e.target.value } })}
                className={INPUT}
              >
                {providers.map(p => (
                  <option key={p.key} value={p.key}>{p.label} ({p.dim}次元)</option>
                ))}
              </select>
            </Field>
          </div>
          <div className='mt-2.5 flex justify-end gap-1.5'>
            <Button color={ButtonColor.SECONDARY} onClick={() => { setDraft({ ...source, config: source.config || {} }); setShowConfig(false) }}>取消</Button>
            <Button color={ButtonColor.PRIMARY} onClick={saveConfig}>✓ 保存</Button>
          </div>
        </div>
      )}
    </div>
  )
}


// =====================
// 検索品質テストオーバーレイ
// =====================
function SearchTestOverlay({ siteId, authHeaders, onClose }) {
  const [query, setQuery] = useState('')
  const [topK, setTopK] = useState(10)
  const [entityType, setEntityType] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)

  const search = async () => {
    if (!query.trim()) return
    setLoading(true)
    setResult(null)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/vector-sources/test-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ query, top_k: topK, entity_type: entityType || null }),
      })
      const d = await r.json()
      setResult(d)
    } catch (e) {
      setResult({ error: e.message })
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose} className='fixed inset-0 z-modal flex items-start justify-center bg-black/60 p-10 pb-20'>
      <div onClick={e => e.stopPropagation()} className={`${CARD} max-h-[85vh] w-full max-w-[720px] overflow-auto p-5`}>
        <div className='mb-3 flex items-center justify-between'>
          <h3 className='text-emphasis m-0 text-sm font-bold'>🔍 ベクトル検索品質テスト</h3>
          <Button color={ButtonColor.SECONDARY} onClick={onClose}>✕</Button>
        </div>
        <p className='text-secondary m-0 mb-2.5 text-[11px]'>
          クエリを入力して、登録済みベクトルから類似度の高い順にヒットを確認できます。
        </p>

        <div className='mb-2 grid grid-cols-[1fr_100px_140px] gap-2'>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder='例: アウトドア用の軽量テント' className={INPUT} />
          <input type='number' min={1} max={50} value={topK} onChange={e => setTopK(parseInt(e.target.value) || 10)} className={INPUT} />
          <select value={entityType} onChange={e => setEntityType(e.target.value)} className={INPUT}>
            <option value=''>全エンティティ</option>
            <option value='product'>商品</option>
            <option value='review'>レビュー</option>
            <option value='category'>カテゴリ</option>
            <option value='feature_page'>特集</option>
          </select>
        </div>
        <Button color={ButtonColor.PRIMARY} disabled={loading || !query.trim()} onClick={search}>
          {loading ? '検索中...' : '🔍 検索実行'}
        </Button>

        {result && (
          <div className='mt-3.5'>
            {result.error ? (
              <div className='bg-surface-critical text-critical rounded-lg p-2.5 text-xs'>
                エラー: {result.error}
              </div>
            ) : (
              <>
                <div className='text-secondary mb-2 text-[11px]'>
                  プロバイダー: <strong>{result.provider_key}</strong> / ヒット: <strong>{result.results_count}</strong> 件
                </div>
                {result.results.length === 0 ? (
                  <div className='text-tertiary p-3.5 text-center text-xs'>
                    該当するベクトルがありません。データソースを同期してから再試行してください。
                  </div>
                ) : (
                  <div className='flex flex-col gap-1.5'>
                    {result.results.map((r, i) => (
                      <div key={i} className='bg-surface-secondary rounded-lg border border-secondary p-2.5'>
                        <div className='mb-1 flex flex-wrap items-center gap-2'>
                          <span className='bg-surface-success text-success rounded px-1.5 py-px font-mono text-[11px] font-bold'>
                            {(r.similarity * 100).toFixed(1)}%
                          </span>
                          <span className='bg-surface-emphasis text-emphasis rounded px-1.5 py-px text-[9px] font-bold'>{r.entity_type}</span>
                          <span className='text-tertiary font-mono text-[10px]'>#{r.entity_id}</span>
                        </div>
                        <div className='text-primary mb-1 text-xs'>{r.text_preview || '(テキストプレビューなし)'}</div>
                        {r.metadata && Object.keys(r.metadata).length > 0 && (
                          <div className='text-tertiary font-mono text-[10px]'>
                            {JSON.stringify(r.metadata).slice(0, 200)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </ModalOverlay>
  )
}


// =====================
// 新規ソース作成オーバーレイ
// =====================
function CreateSourceOverlay({ siteId, authHeaders, providers, defaultProvider, onClose, onCreated }) {
  const [form, setForm] = useState({
    source_type: 'product',
    name: '',
    sync_interval_hours: 24,
    config: { embedding_provider: defaultProvider },
  })
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    if (!form.name.trim()) {
      alert('名前を入力してください')
      return
    }
    setSaving(true)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/vector-sources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(form),
      })
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d.detail || r.status)
      }
      onCreated()
    } catch (e) {
      alert(`作成失敗: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose} className='fixed inset-0 z-modal flex items-center justify-center bg-black/60 p-5'>
      <div onClick={e => e.stopPropagation()} className={`${CARD} w-full max-w-[540px] p-5`}>
        <div className='mb-3 flex items-center justify-between'>
          <h3 className='text-emphasis m-0 text-sm font-bold'>+ 新規データソース</h3>
          <Button color={ButtonColor.SECONDARY} onClick={onClose}>✕</Button>
        </div>
        <div className='flex flex-col gap-2.5'>
          <Field label='種別' required>
            <select value={form.source_type} onChange={e => setForm({ ...form, source_type: e.target.value })} className={INPUT}>
              <option value='product'>商品マスタ</option>
              <option value='brand_category'>ブランド・カテゴリマスタ</option>
              <option value='review'>レビュー / UGC</option>
              <option value='feature_page'>特集ページ</option>
              <option value='custom'>カスタム</option>
            </select>
          </Field>
          <Field label='表示名' required>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder='例: メイン商品マスタ' className={INPUT} />
          </Field>
          <Field label='同期間隔（時間）'>
            <input type='number' min={1} max={168} value={form.sync_interval_hours} onChange={e => setForm({ ...form, sync_interval_hours: parseInt(e.target.value) || 24 })} className={INPUT} />
          </Field>
          <Field label='埋め込みプロバイダー' hint='Phase1ではstubで動作確認可。本番でOpenAI/Voyage等に切替。'>
            <select
              value={form.config.embedding_provider}
              onChange={e => setForm({ ...form, config: { ...form.config, embedding_provider: e.target.value } })}
              className={INPUT}
            >
              {providers.map(p => (
                <option key={p.key} value={p.key}>{p.label} ({p.dim}次元)</option>
              ))}
            </select>
          </Field>
        </div>
        <div className='mt-3.5 flex justify-end gap-1.5'>
          <Button color={ButtonColor.SECONDARY} onClick={onClose}>取消</Button>
          <Button color={ButtonColor.PRIMARY} disabled={saving} onClick={submit}>{saving ? '作成中...' : '+ 作成'}</Button>
        </div>
      </div>
    </ModalOverlay>
  )
}


// =====================
// 共通フィールド
// =====================
function Field({ label, hint, children, required }) {
  return (
    <div className={FIELD}>
      <label className={LABEL}>
        {label}{required && <span className='text-critical ml-0.5'>*</span>}
        {hint && <span className='text-tertiary ml-1.5 text-[10px] font-normal'>— {hint}</span>}
      </label>
      {children}
    </div>
  )
}
